import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Plug, Search, Rocket, Eye, FileSearch, Gauge } from 'lucide-react';
import StatusBadge from './StatusBadge';

const steps = [
  { icon: Plug, title: 'Connect', description: 'Connect your cloud accounts and Kubernetes clusters.' },
  { icon: Search, title: 'Discover', description: 'See the resources and workloads that make up your infrastructure.' },
  { icon: Rocket, title: 'Deploy', description: 'Deploy applications and infrastructure changes through one workflow.' },
  { icon: Eye, title: 'Observe', description: 'Bring metrics, logs, and events together to see system health.' },
  { icon: FileSearch, title: 'Investigate', description: 'Use AI to trace an issue back to its likely cause.' },
  { icon: Gauge, title: 'Optimize', description: 'Get recommendations for improving reliability and efficiency over time.' },
];

const useCases = [
  {
    title: 'Deploy a new application',
    flow: 'Connect infrastructure → configure deployment → deploy → monitor.',
    status: 'in-development' as const,
  },
  {
    title: 'Investigate a production incident',
    flow: 'Alert → inspect affected resources → analyze telemetry → identify likely cause.',
    status: 'coming-soon' as const,
  },
  {
    title: 'Understand Kubernetes health',
    flow: 'Cluster → workloads → events → logs → resource health.',
    status: 'in-development' as const,
  },
];

const Workflow: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="workflow" className="py-32 relative overflow-hidden bg-dark-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-semibold mb-6">
            <span className="text-white">How it&apos;s designed</span>
            <br />
            <span className="gradient-text">to work</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            The target end-to-end workflow, from connecting infrastructure to optimizing it over
            time. Not every step is fully available yet — see individual capability status above.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-20"
        >
          {steps.map((step, index) => (
            <div key={step.title} className="glass rounded-xl p-5 text-center">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-neon-blue rounded-lg flex items-center justify-center mx-auto mb-3">
                <step.icon className="w-5 h-5 text-white" />
              </div>
              <div className="text-xs text-gray-500 mb-1">{index + 1}</div>
              <h3 className="text-sm font-semibold text-white mb-2">{step.title}</h3>
              <p className="text-xs text-gray-400 leading-relaxed">{step.description}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mb-10"
        >
          <h3 className="text-2xl font-semibold text-white">In practice</h3>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {useCases.map((useCase) => (
            <div key={useCase.title} className="feature-card">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-lg font-semibold text-white">{useCase.title}</h4>
                <StatusBadge status={useCase.status} />
              </div>
              <p className="text-sm text-gray-400 font-mono leading-relaxed">{useCase.flow}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Workflow;
